import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { packagesAPI } from '../services/api';
import { Calendar, MapPin, Check, X, ArrowLeft, ChevronLeft, ChevronRight, X as CloseIcon, ZoomIn, Play } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { getOccupancyPercentage, getFutureDatesSorted, isDateUnavailable, isPackageClosed } from '../utils/occupancy';

const SITE_URL = 'https://toindoali.com.br';
const COMPANY_NAME = 'TÔ INDO ALI - Turismo e Aventura';

// Helper to set/update meta tags directly in DOM (more reliable than Helmet for React 19)
const updateMetaTag = (attribute, value, content) => {
  let meta = document.querySelector(`meta[${attribute}="${value}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, value);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const updateLinkTag = (rel, href) => {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

// Helper to check if URL is video
const isVideoUrl = (url) => {
  if (!url) return false;
  const ext = url.split('.').pop()?.toLowerCase().split('?')[0];
  return ['mp4', 'webm', 'mov', 'avi', 'mpeg'].includes(ext);
};

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [cardIndicator, setCardIndicator] = useState('percentage');
  // Modalidade de transporte: 'with_air' (COM AÉREO, padrão) ou 'without_air' (SEM AÉREO).
  // Só é relevante quando o pacote é aéreo E possui air_cost > 0.
  const [transportMode, setTransportMode] = useState('with_air');

  useEffect(() => {
    loadPackage();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/packages/site-settings`)
      .then(r => r.json())
      .then(d => { if (d?.card_indicator) setCardIndicator(d.card_indicator); })
      .catch(() => {});
  }, [id]);

  // Update meta tags when package data changes
  useEffect(() => {
    if (!packageData) return;

    const seoTitle = packageData.seo_title || `${packageData.name} - Pacote de Viagem | ${COMPANY_NAME}`;
    const seoDescription = packageData.seo_description || packageData.short_description || packageData.description?.substring(0, 160) || `Pacote de viagem para ${packageData.name}. Reserve agora com a TÔ INDO ALI!`;
    const slug = packageData.slug || packageData.id;
    const canonicalUrl = `${SITE_URL}/pacote/${slug}`;
    const imageUrl = packageData.main_image || packageData.image || '';

    // Update Open Graph tags
    updateMetaTag('property', 'og:type', 'product');
    updateMetaTag('property', 'og:title', seoTitle);
    updateMetaTag('property', 'og:description', seoDescription);
    updateMetaTag('property', 'og:url', canonicalUrl);
    updateMetaTag('property', 'og:image', imageUrl);
    updateMetaTag('property', 'og:site_name', COMPANY_NAME);

    // Update Twitter Card tags
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', seoTitle);
    updateMetaTag('name', 'twitter:description', seoDescription);
    updateMetaTag('name', 'twitter:image', imageUrl);

    // Update product metadata
    updateMetaTag('property', 'product:price:amount', String(packageData.price || 0));
    updateMetaTag('property', 'product:price:currency', 'BRL');

    // Update canonical URL
    updateLinkTag('canonical', canonicalUrl);

    // Update description meta tag
    updateMetaTag('name', 'description', seoDescription);

    // Cleanup function to reset to defaults when leaving page
    return () => {
      const defaultDescription = 'TÔ INDO ALI - Agência de Turismo e Aventura. Pacotes nacionais e internacionais, turismo de aventura e ecoturismo.';
      updateMetaTag('name', 'description', defaultDescription);
      
      // Remove package-specific meta tags
      ['og:type', 'og:title', 'og:description', 'og:url', 'og:image', 'og:site_name'].forEach(prop => {
        const meta = document.querySelector(`meta[property="${prop}"]`);
        if (meta) meta.remove();
      });
      ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'].forEach(name => {
        const meta = document.querySelector(`meta[name="${name}"]`);
        if (meta) meta.remove();
      });
      ['product:price:amount', 'product:price:currency'].forEach(prop => {
        const meta = document.querySelector(`meta[property="${prop}"]`);
        if (meta) meta.remove();
      });
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, [packageData]);

  // Redirect to slug URL if accessing via ID
  useEffect(() => {
    if (packageData && packageData.slug && id !== packageData.slug) {
      // Replace the current URL with the slug URL (no history entry)
      navigate(`/pacote/${packageData.slug}`, { replace: true });
    }
  }, [packageData, id, navigate]);

  const loadPackage = async () => {
    try {
      setLoading(true);
      const data = await packagesAPI.getById(id);
      setPackageData(data);
    } catch (error) {
      console.error('Error loading package:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get all images (main + gallery) with titles
  const getAllImages = () => {
    if (!packageData) return [];
    const images = [];
    
    // Add main image (check both fields)
    const mainImg = packageData.main_image || packageData.image;
    if (mainImg) {
      images.push({ url: mainImg, title: '' });
    }
    
    // Add gallery images - support both array of strings and array of objects
    if (packageData.gallery_images && packageData.gallery_images.length > 0) {
      packageData.gallery_images.forEach(item => {
        if (typeof item === 'string') {
          images.push({ url: item, title: '' });
        } else if (item && item.url) {
          images.push({ url: item.url, title: item.title || '' });
        }
      });
    }
    
    // Add gallery items (can be objects with url)
    if (packageData.gallery && packageData.gallery.length > 0) {
      packageData.gallery.forEach(item => {
        if (typeof item === 'string') {
          images.push({ url: item, title: '' });
        } else if (item && item.url) {
          images.push({ url: item.url, title: item.title || '' });
        }
      });
    }
    
    return images.filter(img => img && img.url); // Remove null/undefined
  };

  const allImages = getAllImages();

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setShowLightbox(true);
  };

  // Format date with day of week for itinerary
  const formatItineraryDate = (dayNumber) => {
    if (!selectedDate || !selectedDate.start_date) return null;
    
    const startDate = new Date(selectedDate.start_date);
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + dayNumber - 1);
    
    const daysOfWeek = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    
    const dayOfWeek = daysOfWeek[currentDate.getDay()];
    const day = currentDate.getDate();
    const month = months[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    
    return `${dayOfWeek}, ${day} de ${month} de ${year}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-theme-secondary mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando pacote...</p>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Pacote não encontrado</h2>
          <Link to="/pacotes" className="text-[#FFD700] hover:underline">
            Voltar para pacotes
          </Link>
        </div>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Generate SEO metadata
  const getSeoTitle = () => {
    if (!packageData) return 'Carregando... | TÔ INDO ALI';
    return packageData.seo_title || `${packageData.name} - Pacote de Viagem | ${COMPANY_NAME}`;
  };

  const getSeoDescription = () => {
    if (!packageData) return '';
    return packageData.seo_description || packageData.short_description || packageData.description?.substring(0, 160) || `Pacote de viagem para ${packageData.name}. Reserve agora com a TÔ INDO ALI!`;
  };

  const getCanonicalUrl = () => {
    if (!packageData) return `${SITE_URL}/pacote/${id}`;
    const slug = packageData.slug || packageData.id;
    return `${SITE_URL}/pacote/${slug}`;
  };

  // Generate Schema.org JSON-LD
  const getSchemaOrgData = () => {
    if (!packageData) return null;
    
    const minPrice = packageData.price || 0;
    const hasAvailability = packageData.dates?.some(d => d.spots_available > 0) || false;
    const slug = packageData.slug || packageData.id;
    const imageUrl = packageData.main_image || packageData.image;

    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Product",
          "name": packageData.name,
          "description": getSeoDescription(),
          "image": imageUrl,
          "url": `${SITE_URL}/pacote/${slug}`,
          "brand": {
            "@type": "Organization",
            "name": COMPANY_NAME,
            "url": SITE_URL
          },
          "offers": {
            "@type": "Offer",
            "url": `${SITE_URL}/pacote/${slug}`,
            "priceCurrency": "BRL",
            "price": minPrice,
            "availability": hasAvailability ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "seller": {
              "@type": "Organization",
              "name": COMPANY_NAME
            }
          },
          "category": "Travel",
          "sku": `TIA-${packageData.id}`
        },
        {
          "@type": "TravelAgency",
          "name": COMPANY_NAME,
          "url": SITE_URL,
          "description": "Agência de turismo especializada em viagens de aventura e ecoturismo",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "BR"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Início",
              "item": SITE_URL
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Pacotes",
              "item": `${SITE_URL}/pacotes`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": packageData.name,
              "item": `${SITE_URL}/pacote/${slug}`
            }
          ]
        }
      ]
    };
  };

  const handleReservation = () => {
    if (selectedDate && !isDateUnavailable(selectedDate)) {
      const airOffered = !!(packageData?.air_cost && parseFloat(packageData.air_cost) > 0
                             && ['nacional-aereo','internacional-aereo'].includes(packageData?.category));
      const qs = airOffered ? `?transport=${transportMode}` : '';
      navigate(`/checkout/${packageData.id}/${selectedDate.id}${qs}`);
    } else {
      alert('Por favor, selecione uma data antes de continuar');
    }
  };

  // Preço efetivo considerando modalidade COM/SEM AÉREO
  const getEffectivePrice = () => {
    if (!packageData) return 0;
    const base = parseFloat(packageData.price) || 0;
    const airCost = parseFloat(packageData.air_cost) || 0;
    const isAerial = ['nacional-aereo','internacional-aereo'].includes(packageData.category);
    if (isAerial && airCost > 0 && transportMode === 'without_air') {
      return Math.max(0, base - airCost);
    }
    return base;
  };

  const getEffectiveOriginalPrice = () => {
    if (!packageData?.original_price) return null;
    const original = parseFloat(packageData.original_price) || 0;
    const airCost = parseFloat(packageData.air_cost) || 0;
    const isAerial = ['nacional-aereo','internacional-aereo'].includes(packageData.category);
    if (isAerial && airCost > 0 && transportMode === 'without_air') {
      return Math.max(0, original - airCost);
    }
    return original;
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* SEO - Title only (meta tags are handled via useEffect for React 19 compatibility) */}
      <Helmet>
        <title>{getSeoTitle()}</title>
      </Helmet>
      
      {/* Schema.org JSON-LD */}
      {packageData && (
        <script type="application/ld+json">
          {JSON.stringify(getSchemaOrgData())}
        </script>
      )}

      {/* Lightbox Modal */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <CloseIcon className="w-8 h-8" />
          </button>
          
          {/* Counter */}
          <div className="absolute top-4 left-4 text-white text-lg">
            {currentImageIndex + 1} / {allImages.length}
          </div>
          
          {/* Navigation */}
          <button
            onClick={prevImage}
            className="absolute left-4 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          
          <button
            onClick={nextImage}
            className="absolute right-4 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          
          {/* Main Image/Video */}
          {isVideoUrl(allImages[currentImageIndex]?.url) ? (
            <video
              src={allImages[currentImageIndex]?.url}
              controls
              autoPlay
              className="max-h-[85vh] max-w-[90vw] object-contain"
            />
          ) : (
            <img
              src={allImages[currentImageIndex]?.url}
              alt={allImages[currentImageIndex]?.title || `${packageData.name} - Foto ${currentImageIndex + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain"
            />
          )}
          
          {/* Image Title */}
          {allImages[currentImageIndex]?.title && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-lg font-semibold">
              {allImages[currentImageIndex].title}
            </div>
          )}
          
          {/* Thumbnails */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 overflow-x-auto max-w-[80vw] p-2">
            {allImages.map((mediaItem, index) => {
              const isVideo = isVideoUrl(mediaItem.url);
              return (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`relative flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                    index === currentImageIndex ? 'border-theme-secondary' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  {isVideo ? (
                    <video src={mediaItem.url} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={mediaItem.url} alt={mediaItem.title || `Thumb ${index + 1}`} className="w-full h-full object-cover" />
                  )}
                  {isVideo && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play className="w-4 h-4 text-white" fill="white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Hero Image - Single main image */}
      <div className="relative h-64 sm:h-80 md:h-96 overflow-hidden">
        <img
          src={packageData.main_image || packageData.image}
          alt={packageData.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = '/placeholder-package.jpg'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <Link
              to="/pacotes"
              className="inline-flex items-center space-x-2 text-white hover:text-theme-secondary transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Voltar para pacotes</span>
            </Link>
            <h1 className="text-5xl font-bold text-white mb-2">{packageData.name}</h1>
            <p className="text-xl text-gray-200">{packageData.short_description}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Datas Disponíveis */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <Calendar className="w-6 h-6 text-[#FFD700]" />
                  <span>Datas Disponíveis</span>
                </h2>
                <div className="space-y-3">
                  {(() => {
                    const sortedFutureDates = getFutureDatesSorted(packageData?.dates);
                    return sortedFutureDates.map((date, idx) => {
                      const { percentage } = getOccupancyPercentage(
                        date.max_spots, date.spots_available, idx
                      );
                      const isAvailable = !isDateUnavailable(date);
                      const barColor = percentage >= 70 ? 'bg-orange-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-green-500';
                      const trackColor = percentage >= 70 ? 'bg-orange-100' : percentage >= 50 ? 'bg-yellow-100' : 'bg-green-100';

                      return (
                        <div
                          key={date.id}
                          onClick={() => isAvailable && setSelectedDate(date)}
                          data-testid={`date-option-${date.id}`}
                          className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                            !isAvailable
                              ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                              : selectedDate?.id === date.id
                                ? 'border-theme-secondary bg-yellow-50 cursor-pointer'
                                : 'border-gray-200 hover:border-theme-secondary cursor-pointer'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`font-semibold ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>{date.date}</p>
                              {date.badge && (
                                <Badge className="mt-1" variant="outline">{date.badge}</Badge>
                              )}
                            </div>
                            <div className="text-right">
                              {!isAvailable ? (
                                <Badge data-testid={`date-closed-badge-${date.id}`} className="bg-red-100 text-red-800 font-bold">ENCERRADO</Badge>
                              ) : cardIndicator === 'percentage' ? (
                                <div className="flex flex-col items-end gap-1">
                                  <span className="text-sm font-bold text-gray-800">{percentage}% vendido</span>
                                  <div className={`w-24 h-2 ${trackColor} rounded-full overflow-hidden`}>
                                    <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
                                  </div>
                                </div>
                              ) : cardIndicator === 'spots' ? (
                                <span className="text-sm font-bold text-gray-800">
                                  {date.spots_available} {date.spots_available === 1 ? 'vaga' : 'vagas'}
                                </span>
                              ) : cardIndicator === 'days' ? (
                                (() => {
                                  if (!date.start_date) return null;
                                  const now = new Date(); now.setHours(0,0,0,0);
                                  const target = new Date(String(date.start_date).split('T')[0] + 'T00:00:00');
                                  const days = Math.ceil((target - now) / 86400000);
                                  if (days < 0) return null;
                                  const color = days <= 7 ? 'text-red-600' : days <= 30 ? 'text-orange-600' : 'text-gray-800';
                                  return (
                                    <span className={`text-sm font-bold ${color}`}>
                                      {days === 0 ? 'Hoje!' : days === 1 ? 'Amanhã' : `Faltam ${days} dias`}
                                    </span>
                                  );
                                })()
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Descrição Detalhada */}
            {packageData?.description && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Sobre o Destino</h2>
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {packageData.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Roteiro */}
            {packageData?.itinerary && Array.isArray(packageData.itinerary) && packageData.itinerary.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Roteiro Detalhado</h2>
                  {!selectedDate && (
                    <p className="text-amber-600 text-sm mb-4 bg-amber-50 p-3 rounded-lg">
                      💡 Selecione uma data acima para ver as datas específicas de cada dia do roteiro.
                    </p>
                  )}
                  <div className="space-y-6">
                    {packageData.itinerary.map((day) => {
                      const dateStr = formatItineraryDate(day.day);
                      return (
                        <div key={day.day} className="flex space-x-4">
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-theme-secondary rounded-full flex items-center justify-center font-bold text-gray-900">
                              {day.day}
                            </div>
                          </div>
                          <div className="flex-grow">
                            {/* Título: Data/dia da semana substitui o título quando data selecionada */}
                            <h3 className="text-lg font-semibold text-gray-900">
                              {dateStr || `Dia ${day.day}`}
                            </h3>
                            <p className="text-gray-600 mt-1">{day.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Incluso / Não Incluso */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>Incluso</span>
                  </h3>
                  <ul className="space-y-2">
                    {packageData?.included && Array.isArray(packageData.included) && packageData.included.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <X className="w-5 h-5 text-red-600" />
                    <span>Não Incluso</span>
                  </h3>
                  <ul className="space-y-2">
                    {packageData?.not_included && Array.isArray(packageData.not_included) && packageData.not_included.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <X className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Sidebar - Resumo, Reserva e Galeria de Imagens */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Card de Preço e Reserva */}
              <Card>
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <p className="text-sm text-gray-600 mb-2">A partir de</p>
                    {packageData.discount_percentage > 0 && packageData.original_price ? (
                      <>
                        <p className="text-lg text-gray-400 line-through" data-testid="price-original">{formatPrice(getEffectiveOriginalPrice())}</p>
                        <p className="text-4xl font-bold text-green-600" data-testid="price-effective">{formatPrice(getEffectivePrice())}</p>
                        <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-bold">
                          -{packageData.discount_percentage}% OFF
                        </span>
                      </>
                    ) : (
                      <p className="text-4xl font-bold text-theme-primary" data-testid="price-effective">{formatPrice(getEffectivePrice())}</p>
                    )}
                    <p className="text-sm text-gray-600 mt-1">por pessoa</p>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-start space-x-2 text-sm">
                      <MapPin className="w-4 h-4 text-gray-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">Embarque</p>
                        <p className="text-gray-600">{packageData.departure}</p>
                      </div>
                    </div>

                    {selectedDate && (() => {
                      const sortedFutureDates = getFutureDatesSorted(packageData?.dates);
                      const dateIdx = sortedFutureDates.findIndex(d => d.id === selectedDate.id);
                      const { percentage } = getOccupancyPercentage(
                        selectedDate.max_spots, selectedDate.spots_available, dateIdx >= 0 ? dateIdx : 0
                      );
                      const barColor = percentage >= 70 ? 'bg-orange-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-green-500';
                      const trackColor = percentage >= 70 ? 'bg-orange-100' : percentage >= 50 ? 'bg-yellow-100' : 'bg-green-100';

                      let indicator = null;
                      if (cardIndicator === 'percentage') {
                        indicator = (
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className={`flex-1 h-2 ${trackColor} rounded-full overflow-hidden`}>
                              <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
                            </div>
                            <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">{percentage}% vendido</span>
                          </div>
                        );
                      } else if (cardIndicator === 'spots') {
                        indicator = (
                          <p className="text-xs font-semibold text-gray-600 mt-1.5">
                            {selectedDate.spots_available} {selectedDate.spots_available === 1 ? 'vaga disponível' : 'vagas disponíveis'}
                          </p>
                        );
                      } else if (cardIndicator === 'days' && selectedDate.start_date) {
                        const now = new Date(); now.setHours(0,0,0,0);
                        const target = new Date(String(selectedDate.start_date).split('T')[0] + 'T00:00:00');
                        const days = Math.ceil((target - now) / 86400000);
                        if (days >= 0) {
                          const color = days <= 7 ? 'text-red-600' : days <= 30 ? 'text-orange-600' : 'text-gray-700';
                          indicator = (
                            <p className={`text-xs font-semibold mt-1.5 ${color}`}>
                              {days === 0 ? 'Hoje!' : days === 1 ? 'Amanhã' : `Faltam ${days} dias`}
                            </p>
                          );
                        }
                      }

                      return (
                        <div className="flex items-start space-x-2 text-sm bg-yellow-50 p-3 rounded-lg">
                          <Calendar className="w-4 h-4 text-[#FFD700] mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">Data Selecionada</p>
                            <p className="text-gray-600">{selectedDate.date}</p>
                            {indicator}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Escolha COM/SEM AÉREO — só aparece se o pacote é aéreo e tem air_cost cadastrado */}
                  {(() => {
                    const airCost = parseFloat(packageData?.air_cost) || 0;
                    const isAerial = ['nacional-aereo','internacional-aereo'].includes(packageData?.category);
                    if (!isAerial || airCost <= 0) return null;
                    return (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-700 mb-2 text-center uppercase tracking-wide">Modalidade de transporte</p>
                        <div className="grid grid-cols-2 gap-2" data-testid="transport-mode-selector">
                          <button
                            type="button"
                            onClick={() => setTransportMode('with_air')}
                            data-testid="transport-with-air-btn"
                            className={`px-3 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all ${
                              transportMode === 'with_air'
                                ? 'bg-theme-primary text-white border-theme-primary shadow-md'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-theme-primary'
                            }`}
                          >
                            COM AÉREO
                          </button>
                          <button
                            type="button"
                            onClick={() => setTransportMode('without_air')}
                            data-testid="transport-without-air-btn"
                            className={`px-3 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all ${
                              transportMode === 'without_air'
                                ? 'bg-theme-primary text-white border-theme-primary shadow-md'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-theme-primary'
                            }`}
                          >
                            SEM AÉREO
                          </button>
                        </div>
                        {transportMode === 'without_air' && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900" data-testid="without-air-notice">
                            <p className="font-semibold mb-1">⚠️ Contratação SEM TRANSPORTE AÉREO</p>
                            <ul className="list-disc list-inside space-y-0.5 text-[11px]">
                              <li>Você é responsável por chegar ao destino por conta própria.</li>
                              <li>As passagens aéreas NÃO fazem parte da contratação.</li>
                              <li>Aguarde a confirmação da viagem e do quórum mínimo antes de comprar sua passagem.</li>
                              <li>Qualquer passagem adquirida por você é de sua responsabilidade.</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <Button
                    onClick={handleReservation}
                    disabled={isPackageClosed(packageData?.dates)}
                    className="w-full btn-primary-theme font-semibold py-6 text-lg"
                    data-testid="reserve-button"
                  >
                    {isPackageClosed(packageData?.dates) ? 'Vendas Encerradas' : selectedDate ? 'Reservar este Pacote' : 'Selecione uma Data'}
                  </Button>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    Compra 100% segura • Pagamento facilitado
                  </p>
                </CardContent>
              </Card>

              {/* Galeria de Mídia - Formato QUADRADO com Lightbox */}
              {allImages.length > 1 && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-gray-900">Galeria</h3>
                      <button
                        onClick={() => openLightbox(0)}
                        className="text-sm text-theme-primary hover:text-theme-secondary flex items-center"
                      >
                        <ZoomIn className="w-4 h-4 mr-1" />
                        Ver todas
                      </button>
                    </div>
                    
                    {/* Grid de fotos/vídeos quadrados */}
                    <div className="grid grid-cols-3 gap-2">
                      {allImages.slice(0, 6).map((mediaItem, index) => {
                        const isVideo = isVideoUrl(mediaItem.url);
                        return (
                          <button
                            key={index}
                            onClick={() => openLightbox(index)}
                            className="relative aspect-square rounded-lg overflow-hidden group"
                          >
                            {isVideo ? (
                              <video 
                                src={mediaItem.url} 
                                className="w-full h-full object-cover transition-transform group-hover:scale-110"
                                muted
                              />
                            ) : (
                              <img 
                                src={mediaItem.url} 
                                alt={mediaItem.title || `${packageData.name} - Foto ${index + 1}`} 
                                className="w-full h-full object-cover transition-transform group-hover:scale-110" 
                              />
                            )}
                            {isVideo && (
                              <div className="absolute top-1 right-1 bg-black/70 text-white px-1.5 py-0.5 rounded-full flex items-center gap-1 text-xs">
                                <Play className="w-2.5 h-2.5" fill="white" />
                              </div>
                            )}
                            {/* Título da imagem */}
                            {mediaItem.title && (
                              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-1 py-0.5 truncate">
                                {mediaItem.title}
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              {isVideo ? (
                                <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="white" />
                              ) : (
                                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </div>
                            {index === 5 && allImages.length > 6 && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <span className="text-white font-bold text-lg">+{allImages.length - 6}</span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageDetail;
